import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import Table from "../../../components/common/Table/Table";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import CardTable from "../../../components/common/CardTable";
import Pagination from "../../../components/common/Table/Pagination";
import { fetchTuitionApplications, setCurrentPage,  } from "../../../store/slices/Tuition/tuitionViewMyStatusSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import Loader from "../../../components/common/Loader";
import { trackButtonClick, trackNavigation, trackCustomPageView } from '../../../services/analytics';

const TuitionUploadDocument = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get data from Redux store
  const { applications, meta, loading, currentPage, perPage } = useAppSelector(
    (state) => state.tuitionViewMyStatus
  );

  type TableColumn = {
    key: string;
    label: string;
  };

  interface TableRow {
    [key: string]: React.ReactNode;
  }

  const columns: TableColumn[] = [
    { key: "id", label: "Application ID" },
    { key: "applicationDate", label: "Application date" },
    { key: "applicationYear", label: "Year" },
    { key: "applicationTerm", label: "Term" },
    { key: "status", label: "Status" },
    { key: "applicationComment", label: "Comment" },
    { key: "action", label: "Details" },
  ];

  // Fetch applications on component mount or when page changes
  useEffect(() => {
    console.log('🔄 TuitionUploadDocument mounted - fetching applications', { currentPage, perPage });
    dispatch(
      fetchTuitionApplications({
        applType: 'AT001',
        page: currentPage,
        perPage: perPage,
      }) as any
    );
  }, [dispatch, currentPage, perPage]);

  const handleActionClick = (rowData: TableRow) => {
    const id = (rowData as any).id ?? '';
    const createdDate = (rowData as any).applicationDate ??'';
    if (id) {
      console.log('Navigating to details for application ID:', id);
      console.log(`📊 Analytics: View details clicked - Application ${id}`);
      trackButtonClick('Upload documents/View details', 'tuition-upload-document', { applicationId: id });
      trackCustomPageView('my-documents-tab');
      navigate(`/tuition/detailstabs/${encodeURIComponent(String(id))}`,{
        state: { createdDate }
      });
    }
  };

  const handleBreadcrumbPrevious = (): void => {
    console.log('📊 Analytics: Navigate back to tuition');
    trackNavigation('tuition-upload-document', 'tuition', 'back');
    navigate("/tuition");
  };

  const handlePageChange = (page: number) => {
    console.log('📄 Page changed to:', page);
    dispatch(setCurrentPage(page));
  };

  // Transform API data to table format with styled status
  const updatedRows: TableRow[] = applications.map((app) => ({
    id: app.id,
    applicationDate: app.applicationDate,
    applicationYear: app.applicationYear,
    applicationTerm: app.applicationTerm,
    status: (
      <span className="bg-gray-100 border border-[#00000014] rounded px-3 py-1 text-sm font-semibold inline-flex items-center justify-center">
        {app.status}
      </span>
    ),
    applicationComment: app.applicationComment || '-',
    action: "Upload documents/View details",
  }));

  if (loading) {
    return <Loader />;
  }

  let tableContent;
  if (updatedRows.length > 0) {
    tableContent = (
      <>
        <Table
          columns={columns}
          rows={updatedRows}
          onActionClick={handleActionClick}
          isSorting={false}
        />

        {/* Pagination */}
        {meta && (
          <div className="mt-4 mobile:relative mobile:left-[30px]">
            <Pagination
              totalItems={meta.total}
              itemsPerPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </> 
    );
  } else {
    tableContent = (
      <div className="p3 -book text-center mt-2.5! py-8">
        <p>No applications found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen !mt-[60px] lg:!px-[160px] sm:!px-5 mobile:!mx-[16px]">
      <BreadCrumbs
        previousPage="Back to tuition reimbursement"
        currentPage="View my status and upload documents"
        onPreviousClick={handleBreadcrumbPrevious}
        stackOnMobile={true}
      />

      <div>
        <h1 className="h1 tablet:!mx-5 desktop:!mx-5 sm:!mx-0 !mt-[32px]">
          View my status / upload documents
        </h1>
        <p className="p3 -book mb-4!">
          Please select an application to upload documents and view application details
        </p>
      </div>

      <CardTable
        className="mb-8 px-4 mx-5 sm:mx-0 sm:px-0"
        aria-label="My education benefit applications"
      >
        <h3 className="h3 ml-[20px]! mt-[32px]!">My education benefit applications</h3>
        {tableContent}
      </CardTable>

      <ScrollToTopBtn mobileOnly={true} />
    </div>
  );
};

export default TuitionUploadDocument;